import type { ResolvedCitationData } from "@/components/citations";
import type { LiveFinding } from "@/hooks/use-walkthrough-stream";
import type {
  FindingType,
  LiveWalkthroughData,
  Severity,
  UnifiedLiveFinding,
} from "./types";

export function buildUnifiedFindings(
  findings: LiveWalkthroughData["findings"],
  liveFindings: LiveFinding[] | undefined,
  streamLiveFindings: LiveFinding[],
  stepNumberByStepId: Map<string, number>,
  streamSteps?: Array<{
    stepNumber: number;
    findings?: Array<{
      type: string;
      severity: string;
      description: string;
      recommendation?: string | null;
      targetBox?: any | null;
    }>;
  }>,
): UnifiedLiveFinding[] {
  const list: UnifiedLiveFinding[] = [];
  const seenIds = new Set<string>();

  for (const f of findings) {
    if (seenIds.has(f.id) || f.triageStatus === "dismissed") continue;
    seenIds.add(f.id);
    const stepNumber = f.walkthroughStepId
      ? (stepNumberByStepId.get(f.walkthroughStepId) ?? null)
      : null;
    list.push({
      id: f.id,
      title: f.description.split("\n")[0] || f.description,
      type: (f.type as FindingType) || "hinders",
      severity: (f.severity as Severity) || "minor",
      stepNumber,
      description: f.description,
      recommendation: f.recommendation,
      isPreliminary: false,
      triageStatus: f.triageStatus,
      triageNote: f.triageNote,
      source: f.source ?? (f.lens === "designer" ? "designer" : "persona"),
      targetBox: f.targetBox ?? null,
    });
  }

  const liveSources: LiveFinding[] = [
    ...(liveFindings ?? []),
    ...streamLiveFindings,
  ];
  for (const f of liveSources) {
    if (seenIds.has(f.id)) continue;
    seenIds.add(f.id);
    list.push({
      id: f.id,
      title: f.title,
      type: (f.type as FindingType) || "hinders",
      severity: (f.severity as Severity) || "minor",
      stepNumber: f.stepNumber ?? null,
      description: f.consequence || f.title,
      uiFact: f.uiFact,
      consequence: f.consequence,
      isPreliminary: true,
      targetBox: f.targetBox ?? null,
    });
  }

  if (streamSteps) {
    for (const step of streamSteps) {
      if (!step.findings) continue;
      for (let i = 0; i < step.findings.length; i++) {
        const sf = step.findings[i];
        const streamId = `stream-f-${step.stepNumber}-${i}`;
        const alreadyExists = list.some(
          (ex) =>
            ex.stepNumber === step.stepNumber &&
            ex.description === sf.description,
        );
        if (alreadyExists || seenIds.has(streamId)) continue;
        seenIds.add(streamId);
        list.push({
          id: streamId,
          title: sf.description.split("\n")[0] || sf.description,
          type: (sf.type as FindingType) || "hinders",
          severity: (sf.severity as Severity) || "minor",
          stepNumber: step.stepNumber,
          description: sf.description,
          recommendation: sf.recommendation ?? null,
          isPreliminary: true,
          targetBox: sf.targetBox ?? null,
        });
      }
    }
  }

  return list;
}

export function buildSeverityCounts(findings: UnifiedLiveFinding[]): {
  severityCounts: Record<Severity, number>;
  helpsCount: number;
} {
  const severityCounts: Record<Severity, number> = {
    critical: 0,
    major: 0,
    minor: 0,
    nit: 0,
  };
  let helpsCount = 0;
  for (const f of findings) {
    if (f.type === "helps") helpsCount++;
    else if (f.severity in severityCounts) severityCounts[f.severity]++;
  }
  return { severityCounts, helpsCount };
}

export function buildCitationsMap(
  findings: LiveWalkthroughData["findings"],
): Map<string, ResolvedCitationData> {
  const map = new Map<string, ResolvedCitationData>();
  for (const finding of findings) {
    for (const cit of finding.citations) {
      const name = (cit as { resolvedName?: string | null }).resolvedName;
      if (name && cit.sourceId && !map.has(cit.sourceId)) {
        map.set(cit.sourceId, {
          sourceType: cit.sourceType,
          sourceId: cit.sourceId,
          sourceName: name,
          authorityLevel:
            (cit as { resolvedAuthorityLevel?: string | null })
              .resolvedAuthorityLevel ?? "informational",
          contentPreview:
            (cit as { resolvedContent?: string | null }).resolvedContent ?? "",
          characterization: cit.characterization,
          sourceCategory: (cit as { resolvedSourceCategory?: string | null })
            .resolvedSourceCategory,
        });
      }
    }
  }
  return map;
}
